import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Snackbars from "./Snackbars";
import {
  ISnackbarsContext,
  ISnackbar,
  MessageTypeTrigger,
  MessageType,
} from "./types";
import { SupportedFeatures } from "../../features/payslips/services/model/FeatureConfiguration";
import { useDocumentListener } from "../sse/DocumentListener";
import DocumentProcessingJob, {
  DocumentProcessingJobStatus,
} from "../../features/payslips/services/model/DocumentProcessingJob";
import { useMessageTemplates } from "./StrapiCMSApiService";
import { AnalyticsContext } from "../AnalyticsProvider";
import DocumentImporterConfiguration from "../../features/payslips/services/model/DocumentImporterConfiguration";
import { irp5JourneyConfiguration } from "../../features/irp5/irp5JourneyConfiguration";
import { payslipJourneyConfiguration } from "../../features/payslips/payslipJourneyConfiguration";
import { RosterJourneyConfiguration } from "../../features/roster/RosterJourneyConfiguration";
import { TimesheetJourneyConfiguration } from "../../features/timesheet/TimesheetJourneyConfiguration";
import { useEnabledFeatureFlag } from "../../common/feature-flagging/FeatureFlagProvider";
import FeatureFlags from "../../common/feature-flagging/FeatureFlags";

const SnackbarsContext = createContext<ISnackbarsContext>({
  addSnackbar: (): boolean => false,
  removeSnackbar: () => {
    throw new Error("removeSnackbar not implemented");
  },
  removeAllSnackbars: () => {
    throw new Error("removeAllSnackbars not implemented");
  },
  updateSnackbar: () => {
    throw new Error("updateSnackbar not implemented");
  },
  snackbars: [],
});

const SnackbarsProvider: React.FC = ({ children }): JSX.Element => {
  const [snackbars, setSnackbars] = useState<ISnackbar[]>([]);
  const { data: messageTemplates } = useMessageTemplates();
  const { trackDocumentsEvent } = React.useContext(AnalyticsContext);
  const showSnackbars = useEnabledFeatureFlag(FeatureFlags.SNACKBARS);

  const loadSnackbarsFromStorage = (): void => {
    const localSnackbars: ISnackbar[] = JSON.parse(
      localStorage.getItem("snackbars") || "[]"
    );
    const now = new Date();
    const updatedSnackbars = localSnackbars.filter((snackbar) => {
      if (snackbar.expiresAt) {
        return snackbar.expiresAt > now;
      }
      return true;
    });
    const filteredSnackbars = updatedSnackbars.filter((snackbar) => {
      if (snackbar.maxShowCount) {
        return snackbar.showCount && snackbar.showCount < snackbar.maxShowCount;
      }
      return true;
    }); // not implemented
    localStorage.setItem("snackbars", JSON.stringify(filteredSnackbars));
    setSnackbars(filteredSnackbars);
  };

  const addSnackbar = useCallback((newSnackbar: ISnackbar): boolean => {
    let added = false;
    setSnackbars((currentSnackbars) => {
      const forgottenSnackbars = JSON.parse(
        sessionStorage.getItem("forgetSnackbars") || "[]"
      );
      const forgottenSnackbar = forgottenSnackbars.find(
        (s: ISnackbar) => s.id === newSnackbar.id
      );
      if (forgottenSnackbar) {
        return currentSnackbars;
      }

      let existingSnackbarIndex = 0;
      if (newSnackbar.id.toString().includes("-")) {
        const idArray = newSnackbar.id.toString().split("-");
        existingSnackbarIndex = currentSnackbars.findIndex((s) =>
          s.id.toString().includes(idArray[1])
        );
      } else {
        existingSnackbarIndex = currentSnackbars.findIndex(
          (s) => s.id === newSnackbar.id
        );
      }

      if (existingSnackbarIndex === -1) {
        const updatedSnackbars = [newSnackbar, ...currentSnackbars];
        localStorage.setItem("snackbars", JSON.stringify(updatedSnackbars));
        added = true;
        return updatedSnackbars;
      }

      const existingSnackbar = currentSnackbars[existingSnackbarIndex];
      if (
        existingSnackbar.data?.status &&
        existingSnackbar.data?.status === newSnackbar.data?.status
      ) {
        return currentSnackbars;
      }

      const updatedSnackbars = [
        ...currentSnackbars.slice(0, existingSnackbarIndex),
        newSnackbar,
        ...currentSnackbars.slice(existingSnackbarIndex + 1),
      ];
      localStorage.setItem("snackbars", JSON.stringify(updatedSnackbars));
      return updatedSnackbars;
    });

    return added;
  }, []);

  const forgetSnackbar = useCallback((snackbar: ISnackbar): void => {
    const forgottenSnackbars = JSON.parse(
      sessionStorage.getItem("forgetSnackbars") || "[]"
    );
    forgottenSnackbars.push(snackbar);
    sessionStorage.setItem(
      "forgetSnackbars",
      JSON.stringify(forgottenSnackbars)
    );
  }, []);

  const removeSnackbar = useCallback(
    (snackbarId: string | number): void => {
      // get the snackbar with the given ID and remove it from the list
      const snackbar = snackbars.find((s) => s.id === snackbarId);
      if (!snackbar) {
        return;
      }
      const updatedSnackbars = snackbars.filter((s) => s.id !== snackbarId);
      localStorage.setItem("snackbars", JSON.stringify(updatedSnackbars));
      setSnackbars(updatedSnackbars);
      forgetSnackbar(snackbar);
    },
    [snackbars]
  );

  const removeAllSnackbars = (): void => {
    localStorage.setItem("snackbars", JSON.stringify([]));
    setSnackbars([]);
  };

  const updateSnackbar = useCallback(
    (snackbarId: string | number, snackbar: ISnackbar): void => {
      const updatedSnackbars = snackbars.map((s) => {
        if (s.id === snackbarId) {
          return snackbar;
        }
        return s;
      });
      localStorage.setItem("snackbars", JSON.stringify(updatedSnackbars));
      setSnackbars(updatedSnackbars);
    },
    [snackbars]
  );

  const getDocumentConfiguration = (
    docType: SupportedFeatures
  ): DocumentImporterConfiguration | null => {
    switch (docType) {
      case SupportedFeatures.IRP5s:
        return irp5JourneyConfiguration;
      case SupportedFeatures.PAYSLIPS:
        return payslipJourneyConfiguration;
      case SupportedFeatures.ROSTERS:
        return RosterJourneyConfiguration;
      case SupportedFeatures.TIMESHEETS:
        return TimesheetJourneyConfiguration;
      default:
        return null;
    }
  };

  const snackbarDocumentListener = useCallback(
    (doc: DocumentProcessingJob, docType: SupportedFeatures): void => {
      let template: MessageType | undefined;
      let snackbar: ISnackbar;
      const config = getDocumentConfiguration(docType);
      switch (doc.status) {
        case DocumentProcessingJobStatus.MATCHES_NEEDED:
        case DocumentProcessingJobStatus.PARTIALLY_MATCHED:
          template = messageTemplates?.data.find(
            (t) =>
              t.attributes.resource_type === docType &&
              t.attributes.trigger ===
                MessageTypeTrigger.StatusUpdateMatchesNeeded &&
              t.attributes.message_type === "snackbar"
          );
          if (!template) {
            return;
          }
          snackbar = {
            id: `fix-[${doc.id}]`,
            template,
            data: doc,
            type: "warning",
            expiresAt: new Date(Date.now() + 600000),
            maxShowCount: 1,
            showCount: 0,
          };
          if (config && addSnackbar(snackbar)) {
            trackDocumentsEvent({
              event: "Batch_Ready_Unassigned_Snackbar",
              description:
                "User receives a snackbar when their document batch is ready to send and has unassigned documents in the batch.",
              event_type: "Snackbar",
              job: doc,
              type: config.document_name_plural,
            });
          }
          break;
        case DocumentProcessingJobStatus.MATCHED:
          template = messageTemplates?.data.find(
            (t) =>
              t.attributes.resource_type === docType &&
              t.attributes.trigger ===
                MessageTypeTrigger.StatusUpdateReadyToRelease &&
              t.attributes.message_type === "snackbar"
          );
          if (!template) {
            return;
          }
          snackbar = {
            id: `release-[${doc.id}]`,
            template,
            data: doc,
            type: "info",
            expiresAt: new Date(Date.now() + 600000),
            maxShowCount: 1,
            showCount: 0,
          };
          addSnackbar(snackbar);
          if (config) {
            trackDocumentsEvent({
              event: "Batch_Ready_Snackbar",
              description:
                "User receives a snackbar when their document batch is ready to send and has no unassigned documents in the batch.",
              event_type: "Snackbar",
              job: doc,
              type: config.document_name_plural,
            });
          }
          break;
        default:
          break;
      }
    },
    [snackbars]
  );

  useEffect(() => {
    loadSnackbarsFromStorage();
  }, []);

  useDocumentListener(snackbarDocumentListener, [
    DocumentProcessingJobStatus.MATCHES_NEEDED,
    DocumentProcessingJobStatus.PARTIALLY_MATCHED,
    DocumentProcessingJobStatus.MATCHED,
  ]);

  const value = useMemo(
    () => ({
      addSnackbar,
      removeSnackbar,
      removeAllSnackbars,
      updateSnackbar,
      snackbars,
    }),
    [addSnackbar, removeSnackbar, removeAllSnackbars, updateSnackbar, snackbars]
  );

  return (
    <SnackbarsContext.Provider value={value}>
      {showSnackbars && <Snackbars />}
      {children}
    </SnackbarsContext.Provider>
  );
};

const useSnackbars = (): ISnackbarsContext => {
  const context = useContext(SnackbarsContext);
  if (context === undefined) {
    throw new Error("useSnackbars must be used within a SnackbarsProvider");
  }
  return context;
};

export { SnackbarsProvider, useSnackbars, SnackbarsContext };
